import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Layout, theme, Typography } from "antd";
import { useTranslation } from "react-i18next";

import { Roles, UNPRME_FAQ_URL } from "@/shared/utils/constants";
import { useGetDataTemplatesQuery } from "@/redux/service.js";
import { PrivateRoutes } from "@/router/Routes.js";
import {
  PageBannerProvider,
  PageBannerSlot,
} from "@/shared/contexts/PageBannerContext";
import Header from "../header/Header.jsx";
import MobileRedirect from "@/features/MobileRedirect.jsx";
import { useResponsive } from "@/shared/hooks/useBreakpoints.js";
import SidebarMenu from "../../components_v2/Menu/SidebarMenu.js";
import PrmeLogo from "@/assets/icons/prme-logo.png";
import { selectCurrentUser } from "@/redux/authentication/selectors.js";
import { LocalStorageName } from "@/redux/api.ts";
import { setAuthenticationState } from "@/redux/authentication/reducer.js";

import "./styles.css";

const { Title } = Typography;
const { Footer, Content, Sider } = Layout;

const AuthenticatedLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const dispatch = useDispatch();

  const currentUser = useSelector(selectCurrentUser);
  const { t } = useTranslation();
  const [isUserAllowedToView, setIsUserAllowedToView] = useState(false);
  const { isMobile } = useResponsive();

  // Get static data
  useGetDataTemplatesQuery(
    { pagination: "off", isEvidence: true },
    {
      pollingInterval: 0,
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    },
  );

  useEffect(() => {
    if (currentUser) {
      checkIsCurrentUserAllowedToView();
    } else {
      window.localStorage.removeItem(LocalStorageName);
      dispatch(setAuthenticationState({ isAuthenticated: false, user: null }));
      navigate(`/login?redirect_to=${location.pathname}`);
    }
  }, [currentUser]);

  const checkIsCurrentUserAllowedToView = () => {
    const currentRoute = PrivateRoutes.find((i) => {
      const pattern = i.path.replace(/:\w+/g, "[^/]+");
      const regex = new RegExp(`^${pattern}$`);
      return regex.test(location.pathname);
    });

    if (currentRoute) {
      if (
        currentRoute?.roles &&
        !currentRoute.roles.includes(currentUser.role) &&
        !currentRoute?.organizationRoles
      ) {
        navigate("/");
      }

      if (
        currentRoute?.organizationRoles &&
        !currentRoute.organizationRoles.includes(currentUser.organizationRole)
      ) {
        navigate("/");
      }
    }
    setIsUserAllowedToView(true);
  };

  if (isMobile) {
    return <MobileRedirect />;
  }

  return (
    <Layout
      style={{
        minHeight: "100vh",
      }}
    >
      <Sider
        breakpoint="md"
        collapsedWidth="0"
        width={isMobile ? "85vw" : 250}
        className="side-menu"
      >
        <div className="side-menu-logo-container">
          <img className="side-menu-logo" src={PrmeLogo} alt={t("alts.logo")} />
        </div>

        {currentUser ? (
          <div>
            {currentUser.role === Roles.ADMIN.value && (
              <div
                className="mt-4 py-2 text-center"
                style={{ backgroundColor: token.colorTextThird }}
              >
                <Title
                  level={5}
                  style={{
                    fontSize: "17px",
                    color: "white",
                    fontWeight: "300",
                  }}
                >
                  {t("prmeSecretariat")}
                </Title>
              </div>
            )}
            <SidebarMenu />
          </div>
        ) : null}
      </Sider>

      <Layout>
        {currentUser && <Header user={currentUser} />}
        <PageBannerProvider>
          <PageBannerSlot />
          <Content>
            <div
              className={`${location.pathname === "/" ? "h-100" : "main-content-container"}`}
            >
              {isUserAllowedToView && <Outlet />}
            </div>
          </Content>
        </PageBannerProvider>

        <Footer
          id="footer"
          style={{
            padding: "7px 0",
            textAlign: "center",
            borderTop: "1px solid rgba(198, 206, 220, 1)",
          }}
        >
          <Link
            to={"/privacy-policy"}
            style={{ color: "black", textDecoration: "underline" }}
          >
            {t("privacyPolicy")}
          </Link>
          <span className="mx-3">|</span>
          <Link
            to={UNPRME_FAQ_URL}
            target="_blank"
            style={{ color: "black", textDecoration: "underline" }}
          >
            {t("faqs")}
          </Link>
          <span className="mx-3">|</span>
          <Link
            to="https://prme.storylane.io/share/iuag9sbcz72f?step=15"
            target="_blank"
            rel="noreferrer"
            style={{ color: "black", textDecoration: "underline" }}
          >
            {t("demoTutorial")}
          </Link>
        </Footer>
      </Layout>
    </Layout>
  );
};

export default AuthenticatedLayout;
